<template>
    <div>
        <ObjectHousesFilter
            v-if="isFiltersReady"
            class="ml-3"
            :filter-model.sync="filter"
            :contracts="contractOptions"
            :objects="objectOptions"
            :categories="categoryOptions"
            @resetFilter="resetFilter"
            @applyFilter="applyFilter"
        />
        <v-btn color="success" class="ml-3 mt-4" :disabled="!OHLT.length" @click="exportToExcel">
            <v-icon left>mdi-file-excel</v-icon>
            Выгрузить в Excel
        </v-btn>
        <v-data-table
            class="mt-5"
            :headers="headers"
            :items="OHLT"
            :options.sync="objectHousePagination"
            :server-items-length="ObjectHouseListLength"
            :loading="ObjectHouseLoading"
            :footer-props="{ 'items-per-page-options': $vuetify.breakpoint.xsOnly ? [1] : [10, 15, 20, 25] }"
        >
            <template v-slot:item.is_formed="{ item }">
                <v-checkbox :input-value="!!item.is_formed" readonly disabled hide-details class="ma-0 pa-0" />
            </template>
            <template v-slot:item.contractor_name="{ item }">
                <span v-if="item.contract_number">
                    <strong>{{ item.contract_number }}</strong>
                    <span v-if="item.contractor_name"> — {{ item.contractor_name }}</span>
                </span>
                <span v-else>
                    {{ item.contractor_name || '—' }}
                </span>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import translate from '@/utils/translate.util';
import { mapState, mapActions } from 'vuex';
import ObjectHousesFilter from '@/components/ObjectHousesFilter.vue';
import * as XLSX from 'xlsx';

export default {
    name: 'ObjectHouses',
    components: { ObjectHousesFilter },
    data: () => ({
        isFiltersReady: false,
        engineer_rid: null,
        filter: {
            contract_rid: null,
            category_rid: null,
            object_rid: null,
            address_full_name___ru: null,
            is_formed: null
        }
    }),
    computed: {
        ...mapState('prescription_ozp', [
            'ObjectHousePagination',
            'ObjectHouseList',
            'ObjectHouseListLength',
            'ObjectHouseLoading',
            'EngineerContractsList',
            'EngineerObjectsList',
            'ConsumptionCategoryList'
        ]),
        ...mapState('users', ['userInfo']),
        categoryOptions() {
            const list = Array.isArray(this.ConsumptionCategoryList) ? this.ConsumptionCategoryList : [];

            return list.map(item => ({
                category_rid: Number(item.record_id),
                category_name: item.consumption_category_name___ru
            }));
        },
        objectOptions() {
            const list = Array.isArray(this.EngineerObjectsList) ? this.EngineerObjectsList : [];
            return list.map(o => ({ object_rid: Number(o.object_rid), object_name: String(o.object_name___ru ?? o.object_name ?? '') }));
        },
        contractOptions() {
            const list = Array.isArray(this.EngineerContractsList) ? this.EngineerContractsList : [];
            return list.map(c => ({ contract_rid: Number(c.contract_rid), contract_number: String(c.contract_number) }));
        },
        headers: function() {
            return [
                {
                    text: this.$t('Категория потребителя'),
                    sortable: false,
                    value: 'consumption_category'
                },
                {
                    text: this.$t('Наименование объекта'),
                    sortable: false,
                    value: 'info_name'
                },
                {
                    text: this.$t('Номер договора, Наименование договора'),
                    sortable: false,
                    value: 'contractor_name'
                },
                {
                    text: this.$t('Адрес'),
                    sortable: false,
                    value: 'address_full_name'
                },
                {
                    text: this.$t('Административный район'),
                    sortable: false,
                    value: 'district_name'
                },
                {
                    text: this.$t('Контакты'),
                    sortable: false,
                    value: 'contacts'
                },
                {
                    text: this.$t('Выдано предписание'),
                    sortable: false,
                    value: 'is_formed'
                }
            ];
        },
        OHLT() {
            return translate(this.ObjectHouseList, 'full', this.$i18n.locale);
        },
        objectHousePagination: {
            get() {
                return this.ObjectHousePagination;
            },
            async set(val) {
                this.$store.commit('prescription_ozp/setObjectHousePagination', val);
                this.loadTable();
            }
        }
    },
    methods: {
        ...mapActions('prescription_ozp', ['getObjectsHouses', 'getEngineerContractsList', 'getEngineerObjectsList', 'getConsumptionCategory']),
        ...mapActions('users', ['getUserInfo']),
        async loadTable() {
            if (this.engineer_rid) {
                await this.getObjectsHouses({
                    engineer_rid: this.engineer_rid,
                    filter: { ...this.filter },
                    itemsPerPage: this.ObjectHousePagination.itemsPerPage,
                    currentItem: this.ObjectHousePagination.itemsPerPage * (this.ObjectHousePagination.page - 1)
                });
            }
        },
        applyFilter() {
            this.ObjectHousePagination.page = 1;
            this.loadTable();
        },
        resetFilter() {
            this.itemStart = 0;
            this.filter = {
                category_rid: null,
                object_rid: null,
                contract_rid: null,
                address_full_name___ru: null,
                is_formed: null
            };
            this.loadTable();
        },
        setIssued(value, checked) {
            this.filter.is_formed = checked ? (value ? 1 : 0) : null;
        },
        exportToExcel() {
            const rows = this.OHLT.map(item => ({
                'Категория потребителя': item.consumption_category || '',
                'Наименование объекта': item.info_name || '',
                'Номер договора, Наименование договора': item.contract_number
                    ? `${item.contract_number}${item.contractor_name ? ' — ' + item.contractor_name : ''}`
                    : item.contractor_name || '',
                Адрес: item.address_full_name || '',
                'Административный район': item.district_name || '',
                Контакты: item.contacts || '',
                'Выдано предписание': item.is_formed ? 'Да' : 'Нет'
            }));

            const worksheet = XLSX.utils.json_to_sheet(rows);

            worksheet['!cols'] = [{ wch: 25 }, { wch: 35 }, { wch: 45 }, { wch: 45 }, { wch: 25 }, { wch: 30 }, { wch: 20 }];

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Объекты');

            XLSX.writeFile(workbook, 'Объекты_дома.xlsx');
        }
    },
    async created() {
        await this.getUserInfo();

        if (this.userInfo && this.userInfo.user_data && this.userInfo.user_data.isas_engineer_rid) {
            this.engineer_rid = this.userInfo.user_data.isas_engineer_rid;
        }

        await Promise.all([
            this.getEngineerContractsList({ iengineer_rid: Number(this.engineer_rid) || 0 }),
            this.getEngineerObjectsList({ iengineer_rid: Number(this.engineer_rid) || 0 }),
            this.getConsumptionCategory()
        ]);

        this.isFiltersReady = true;

        await this.loadTable();
    }
};
</script>
